import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button } from '@mantine/core';
import MantineTable from '../../../../common/table/mantine';
import { axios } from '../../../../utils/axios';
import { useParams } from 'react-router-dom';
import { Status } from '../../styles';
import { CostingRequestsContext } from '../../../../contexts/CostingRequest';
import AssignedUsersList from '../../../../components/assignedUsersList';
import { crRoles } from '../../../../constants';
import { AppLoader } from '../../../../components/common/loader';
import { showAlert } from '../../../../utils/alerts';

export const PvLine = ({ setLogsModal, activeTab }) => {
  const { crNumber } = useParams();
  const [retryLoading, setRetryLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [skus, setSkus] = useState([]);
  const { isCrRoleAuthorized, crAssignedSpocs } = useContext(CostingRequestsContext);

  useEffect(() => {
    if (activeTab === 'pvLine') fetch();
  }, [activeTab]);

  const fetch = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/costing-request/step/pv-line-item-check/${crNumber}`);
      const data = response.data.data.map((item) => {
        const status = (item.status || '').toLowerCase();
        return {
          id: item.id,
          sku_number: item.int_cr_sku_selector.sku,
          sku_description: item.int_cr_sku_selector.sku_description,
          producing_plant: item.int_cr_producing_plant.producing_plant,
          pvline_available: ['manually closed', 'closed'].includes(status) ? 'Yes' : 'No',
          status:
            status === 'not started'
              ? 'Not Started'
              : ['manually closed', 'closed'].includes(status)
              ? 'Completed'
              : status === 'force closed'
              ? 'Force Closed'
              : status === 'force cancelled'
              ? 'Force Cancelled'
              : 'Pending',
          manually_closed: status === 'manually closed',
          logs: item.IntCrStepStatusLogs,
          assignedTo: crAssignedSpocs(item.int_cr_producing_plant.producing_plant),
          spoc: crAssignedSpocs(item.int_cr_producing_plant.producing_plant, crRoles.scheduling),
          step_id: item.step_id,
        };
      });
      setSkus(data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async (stepId) => {
    try {
      setRetryLoading(true);
      const payload = {
        row_id: stepId,
      };
      const response = await axios.post(`/costing-request/step/create_pv/${crNumber}`, payload);

      if (response.status === 200) {
        showAlert(
          {
            message: `PV Line successfully updated`,
          },
          'success',
        );
      } else {
        throw new Error(response.data.message || 'Failed to refresh costing request');
      }
    } catch (error) {
      showAlert(
        {
          message: `Failed to update PV Line. Please check logs`,
        },
        'error',
      );
    } finally {
      await fetch();
      setRetryLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'action',
        header: 'Action',
        Cell: ({ row }) => {
          const id = row.original?.id;
          const plant = row.original?.producing_plant;
          const status = row.original?.status;
          const hasRefreshAccess = isCrRoleAuthorized(String(plant));

          const isDisabled =
            ['Completed', 'Not Started', 'Force Closed', 'Force Cancelled'].includes(status) ||
            !hasRefreshAccess ||
            retryLoading;
          return (
            <Button
              variant='subtle'
              color='dark'
              size='sm'
              style={{ width: '100%' }}
              onClick={() => handleRefresh(id)}
              disabled={isDisabled}
            >
              {retryLoading ? <AppLoader /> : 'Retry'}
            </Button>
          );
        },
      },
      {
        accessorKey: 'sku_number',
        header: 'SKU No',
      },
      {
        accessorKey: 'sku_description',
        header: 'SKU Description',
      },
      {
        accessorKey: 'producing_plant',
        header: 'Production Plant',
      },
      {
        accessorKey: 'pvline_available',
        header: 'PV Line Available?',
      },

      {
        id: 'status',
        header: 'Status',
        Cell: ({ row }) => <Status status={row.original.status} />,
      },
      {
        id: 'logs',
        header: 'Logs',
        Cell: ({ row }) => (
          <Button
            color='dark'
            onClick={() =>
              setLogsModal({
                open: true,
                logs: row.original.logs,
                type: 'PV Line Item Check',
              })
            }
          >
            View Logs
          </Button>
        ),
      },
      {
        accessorKey: 'assignedTo',
        header: 'FP&A GCC SME',
        Cell: ({
          row: {
            original: { assignedTo },
          },
        }) => <AssignedUsersList users={assignedTo} />,
      },
      {
        accessorKey: 'spoc',
        header: 'Scheduling Team SPOC',
        Cell: ({
          row: {
            original: { spoc },
          },
        }) => <AssignedUsersList users={spoc} />,
      },
    ],
    [skus],
  );

  return (
    <>
      <MantineTable
        columns={columns}
        initialData={skus}
        unique={'sku'}
        enableRowSelection={false}
        loading={loading}
        showSelectedToggle={false}
        hideSelectColumn={true}
        showResetAll={false}
        enableExport={true}
        initialState={{
          sorting: [
            {
              id: 'sku_number',
              desc: false,
            },
          ],
        }}
        csvFilename={`pv_line_item_check_${moment().format('YYYY-MM-DD')}`}
        pageSize={10}
      />
    </>
  );
};
